import * as Sentry from '@sentry/browser';

import type { paths } from '@luxuryescapes/contract-svc-content';

import type { TermsAndConditionsBrand } from '~/consts/termsAndConditions';

import ApiError from '~/types/ApiError';

import { RequestOptions, json_headers, request } from './common';

const BASE_URL = `${window.configs.API_HOST}/api/content`;

export const getContentComponentTagBySlug = async (slug: string, brand = 'luxuryescapes') => {
  const response = await request(
    `${BASE_URL}/componentTag/${slug}?date=${new Date().getTime()}&withStatus=true&brand=${brand}`,
    {
      method: 'GET',
    },
  );
  const fields = (response.result[0] as { fields?: Record<string, unknown> })?.fields ?? {};
  return { ...fields, status: response.result[0].status };
};

export type CreateContentComponentTagBodyParams =
  paths['/api/content/componentTag']['post']['requestBody']['content']['application/json'];

export const createContentComponentTag = async (componentTagParams: CreateContentComponentTagBodyParams) => {
  const response = await request(`${BASE_URL}/componentTag`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(componentTagParams),
  });
  return response.result.id;
};

export type UpdateContentComponentTagBodyParams =
  paths['/api/content/componentTag/{id}']['put']['requestBody']['content']['application/json'];

export const updateContentComponentTag = async (
  id: string,
  componentTagParams: UpdateContentComponentTagBodyParams,
) => {
  return request(`${BASE_URL}/componentTag/${id}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(componentTagParams),
  });
};

export const deleteContentComponentTag = async (id: string) => {
  return request(`${BASE_URL}/componentTag/${id}`, {
    method: 'DELETE',
  });
};

export const updateContentComponentTagStatus = async (id: string, status: string) => {
  return request(`${BASE_URL}/componentTag/${id}/status`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify({ status }),
  });
};

const TERMS_AND_CONDITIONS_URL = `${BASE_URL}/terms-and-conditions`;

export type TermsAndConditionsSummary =
  paths['/api/content/terms-and-conditions/{brand}']['get']['responses'][200]['content']['application/json']['result'][number];

export type TermsAndConditions =
  paths['/api/content/terms-and-conditions/{brand}/{slug}']['get']['responses'][200]['content']['application/json']['result'];

export type SaveTermsAndConditionsBodyParams =
  paths['/api/content/terms-and-conditions/{brand}/{slug}']['put']['requestBody']['content']['application/json'];

export const listTermsAndConditions = async (
  brand: TermsAndConditionsBrand,
  options: RequestOptions = {},
): Promise<Array<TermsAndConditionsSummary>> => {
  const response = await request(`${TERMS_AND_CONDITIONS_URL}/${brand}`, { ...options, method: 'GET' });
  return response.result;
};

/**
 * Returns null rather than the en-AU fallback the endpoint serves for an untranslated
 * locale, so no caller renders or re-saves English as though it were a translation.
 */
export const getTermsAndConditions = async (
  brand: TermsAndConditionsBrand,
  slug: string,
  locale: string,
  options: RequestOptions = {},
): Promise<TermsAndConditions | null> => {
  const cacheBuster = `_=${Date.now()}`;

  const response: Response = await request(
    `${TERMS_AND_CONDITIONS_URL}/${brand}/${encodeURIComponent(slug)}?${cacheBuster}`,
    {
      ...options,
      method: 'GET',
      cache: 'no-store',
      headers: { 'Accept-Language': locale },
      returnResponse: true,
      allowNonOkResponse: true,
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }

    const errorBody = await response.json().catch(() => undefined);
    const error = new ApiError({
      message: `Failed to load ${slug} in ${locale}${errorBody?.message ? `: ${errorBody.message}` : ''}`,
      name: 'NetworkError',
      status: response.status,
      responseBody: errorBody,
    });
    Sentry.captureException(error);
    throw error;
  }

  const body = await response.json();
  return body.result?.locale === locale ? body.result : null;
};

export const saveTermsAndConditions = async (
  brand: TermsAndConditionsBrand,
  slug: string,
  params: SaveTermsAndConditionsBodyParams,
): Promise<TermsAndConditions> => {
  const response = await request(`${TERMS_AND_CONDITIONS_URL}/${brand}/${encodeURIComponent(slug)}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(params),
  });
  return response.result;
};
